/**
 * API service for absence email template and rule management (E19-5: MED-501)
 * Updated for dynamic tier-based escalation (MED-543)
 */

import { apiClient } from './client';

// ============================================================
// Types
// ============================================================

export type EscalationTier =
  | 'first_warning'
  | 'first_warning_with_offer'
  | 'mid_escalation'
  | 'deferral_offer'
  | 'forced_deferral';

export interface AbsenceEmailTemplate {
  id: string;
  name: string;
  subject_template: string;
  body_template: string;
  escalation_tier: EscalationTier;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AbsenceEmailTemplateWithRuleCount extends AbsenceEmailTemplate {
  rule_count: number;
  rule_summaries: string[];
}

export interface CreateTemplateInput {
  name: string;
  subject_template: string;
  body_template: string;
  escalation_tier: EscalationTier;
  is_active?: boolean;
}

export interface UpdateTemplateInput {
  name?: string;
  subject_template?: string;
  body_template?: string;
  escalation_tier?: EscalationTier;
  is_active?: boolean;
}

export type AbsenceRuleType = 'unexcused' | 'business' | 'personal' | 'any';

export interface AbsenceEmailRule {
  id: string;
  escalation_tier: EscalationTier;
  absence_type: AbsenceRuleType;
  is_first_session: boolean;
  no_email: boolean;
  template_id: string | null;
  priority: number;
  created_at: string;
  updated_at: string;
}

export interface AbsenceEmailRuleWithTemplateName extends AbsenceEmailRule {
  template_name: string | null;
}

export interface CreateRuleInput {
  escalation_tier: EscalationTier;
  absence_type: AbsenceRuleType;
  is_first_session: boolean;
  no_email: boolean;
  template_id: string | null;
  priority: number;
}

export interface UpdateRuleInput {
  escalation_tier?: EscalationTier;
  absence_type?: AbsenceRuleType;
  is_first_session?: boolean;
  no_email?: boolean;
  template_id?: string | null;
  priority?: number;
}

export interface CoverageGap {
  escalation_tier: string;
  absence_type: string;
  is_first_session: boolean;
  description: string;
}

export interface TierCoverageResult {
  escalation_tier: string;
  covered: boolean;
  gaps: CoverageGap[];
  total_rules: number;
}

export interface CoverageAnalysis {
  tiers: TierCoverageResult[];
  all_covered: boolean;
}

// ============================================================
// Template API
// ============================================================

export const absenceEmailTemplatesApi = {
  list: async (): Promise<AbsenceEmailTemplateWithRuleCount[]> => {
    return apiClient.get<AbsenceEmailTemplateWithRuleCount[]>('/api/absence-email-templates');
  },

  getById: async (id: string): Promise<AbsenceEmailTemplate> => {
    return apiClient.get<AbsenceEmailTemplate>(`/api/absence-email-templates/${id}`);
  },

  create: async (data: CreateTemplateInput): Promise<AbsenceEmailTemplate> => {
    return apiClient.post<AbsenceEmailTemplate>('/api/absence-email-templates', data);
  },

  update: async (id: string, data: UpdateTemplateInput): Promise<AbsenceEmailTemplate> => {
    return apiClient.patch<AbsenceEmailTemplate>(`/api/absence-email-templates/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    return apiClient.delete<void>(`/api/absence-email-templates/${id}`);
  },
};

// ============================================================
// Rule API
// ============================================================

export const absenceEmailRulesApi = {
  list: async (escalationTier?: string): Promise<AbsenceEmailRuleWithTemplateName[]> => {
    const params = escalationTier ? { escalation_tier: escalationTier } : undefined;
    return apiClient.get<AbsenceEmailRuleWithTemplateName[]>('/api/absence-email-rules', params);
  },

  create: async (data: CreateRuleInput): Promise<AbsenceEmailRule> => {
    return apiClient.post<AbsenceEmailRule>('/api/absence-email-rules', data);
  },

  update: async (id: string, data: UpdateRuleInput): Promise<AbsenceEmailRule> => {
    return apiClient.patch<AbsenceEmailRule>(`/api/absence-email-rules/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    return apiClient.delete<void>(`/api/absence-email-rules/${id}`);
  },

  getCoverage: async (): Promise<CoverageAnalysis> => {
    return apiClient.get<CoverageAnalysis>('/api/absence-email-rules/coverage');
  },
};
